import React, { useEffect, useCallback } from 'react';
import { Box, Button, Typography, Fade, Portal } from '@mui/material';
import ZoomInMapOutlinedIcon from '@mui/icons-material/ZoomInMapOutlined';
import { isMacPlatform } from '../../utils/browserZoomOnboarding';

interface BrowserZoomOnboardingProps {
  open: boolean;
  onClose: () => void;
}

const BrowserZoomOnboarding: React.FC<BrowserZoomOnboardingProps> = ({ open, onClose }) => {
  const isMac = isMacPlatform();
  const modKey = isMac ? '⌘' : 'Ctrl';

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <Portal>
      <Fade in={open} timeout={400}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="klinvia-zoom-onboarding-title"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (t) => t.zIndex.modal + 12,
            pointerEvents: 'auto',
          }}
        >
          {/* Escurece a tela */}
          <Box
            onClick={handleClose}
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(11, 18, 32, 0.78)',
              backdropFilter: 'blur(2px)',
            }}
          />

          {/* Destaque no canto superior direito (controles de zoom do navegador) */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 72,
              width: 132,
              height: 40,
              borderRadius: 2,
              border: '2px solid rgba(45, 212, 191, 0.95)',
              boxShadow: '0 0 0 6px rgba(13, 148, 136, 0.22), 0 8px 32px rgba(0,0,0,0.35)',
              bgcolor: 'rgba(255,255,255,0.08)',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              animation: 'klinviaZoomPulse 2.4s ease-in-out infinite',
              '@keyframes klinviaZoomPulse': {
                '0%, 100%': { boxShadow: '0 0 0 6px rgba(13, 148, 136, 0.22), 0 8px 32px rgba(0,0,0,0.35)' },
                '50%': { boxShadow: '0 0 0 10px rgba(13, 148, 136, 0.32), 0 8px 32px rgba(0,0,0,0.35)' },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          >
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', opacity: 0.85 }}>−</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#2DD4BF', letterSpacing: '0.04em' }}>100%</Typography>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', opacity: 0.85 }}>+</Typography>
          </Box>

          {/* Balão explicativo */}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: { md: 72, lg: 80 },
              right: { xs: 16, md: 24 },
              left: { xs: 16, md: 'auto' },
              width: { xs: 'auto', md: 360 },
              maxWidth: 400,
            }}
          >
            {/* Seta apontando para o canto superior direito */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: -28,
                right: 118,
                width: 0,
                height: 0,
                borderLeft: '12px solid transparent',
                borderRight: '12px solid transparent',
                borderBottom: '14px solid #fff',
                filter: 'drop-shadow(0 -2px 2px rgba(15,23,42,0.08))',
              }}
            />

            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 2.5,
                boxShadow: '0 20px 50px rgba(15, 23, 42, 0.28)',
                border: '1px solid rgba(37, 99, 235, 0.12)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2.25,
                  py: 1.5,
                  background: 'linear-gradient(135deg, rgba(13,148,136,0.12) 0%, rgba(37,99,235,0.08) 100%)',
                  borderBottom: '1px solid rgba(148,163,184,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(13,148,136,0.14)',
                    color: '#0D9488',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ZoomInMapOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography
                    id="klinvia-zoom-onboarding-title"
                    sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0B1220', lineHeight: 1.25 }}
                  >
                    Ajuste o zoom do navegador
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.25 }}>
                    Primeira vez neste navegador
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: 2.25, py: 2 }}>
                <Typography sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.55, mb: 1.5 }}>
                  Monitores variam muito de tamanho. Calibre o zoom do navegador para ver mais conteúdo com conforto
                  — agenda, prontuário e financeiro ficam mais legíveis quando a escala está certa para a sua tela.
                </Typography>

                <Box component="ul" sx={{ m: 0, pl: 2.25, color: '#475569', fontSize: '0.8rem', lineHeight: 1.6 }}>
                  <li>
                    Use <strong>{modKey} + scroll</strong> ou <strong>{modKey} + + / −</strong> no teclado.
                  </li>
                  <li>
                    No Chrome ou Edge, os botões <strong>− / +</strong> ficam no canto superior direito (seta acima).
                  </li>
                  <li>
                    Experimente entre <strong>75% e 100%</strong> até a interface ficar confortável.
                  </li>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    mt: 2,
                    py: 1.05,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    bgcolor: '#0D9488',
                    boxShadow: '0 4px 14px rgba(13,148,136,0.35)',
                    '&:hover': { bgcolor: '#0F766E' },
                  }}
                >
                  Entendi, vou ajustar
                </Button>
                <Typography sx={{ mt: 1, fontSize: '0.72rem', color: '#94A3B8', textAlign: 'center' }}>
                  Esta dica aparece apenas uma vez neste navegador.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Portal>
  );
};

export default BrowserZoomOnboarding;
